import CloseIcon from "@mui/icons-material/Close";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ErrorIcon from "@mui/icons-material/Error";
import HistoryToggleOffIcon from "@mui/icons-material/HistoryToggleOff";
import MoneyOffCsredIcon from "@mui/icons-material/MoneyOffCsred";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { CircularProgress } from "@mui/material";
import { useMemo } from "react";
import type { TestRunStatus } from "../types";
import { StatusChip, type StatusEntry } from "./ui/status-chip";

const STATUS_MAP: Partial<Record<TestRunStatus, StatusEntry>> = {
  running: {
    icon: <CircularProgress size={12} color="inherit" />,
    color: "primary",
  },
  pending: {
    icon: <CircularProgress size={12} color="inherit" />,
    color: "default",
  },
  error: { icon: <ErrorIcon sx={{ fontSize: 12 }} />, color: "error" },
  passed: { icon: <DoneAllIcon sx={{ fontSize: 12 }} />, color: "success" },
  failed: { icon: <CloseIcon sx={{ fontSize: 12 }} />, color: "error" },
  failed_pending: { icon: <CloseIcon sx={{ fontSize: 12 }} />, color: "error" },
  flaky: { icon: <VisibilityIcon sx={{ fontSize: 12 }} />, color: "success" },
  skipped: { color: "info" },
  cancelled: { icon: <DoDisturbIcon sx={{ fontSize: 12 }} />, color: "info" },
  timed_out: {
    icon: <HistoryToggleOffIcon sx={{ fontSize: 12 }} />,
    color: "error",
  },
  no_credits: {
    icon: <MoneyOffCsredIcon sx={{ fontSize: 12 }} />,
    color: "error",
  },
};

type Props = {
  status: TestRunStatus;
  variant?: "outlined" | "filled";
  size?: "small" | "medium";
  label?: string;
  retryPhase?: "initial" | "fixing";
};

export const RunStatusChip: React.FC<Props> = ({
  status,
  variant = "outlined",
  size = "small",
  label,
  retryPhase,
}) => {
  const retrySx = useMemo(() => {
    if (!retryPhase) return undefined;
    const hex = retryPhase === "initial" ? "#f9a825" : "#7b1fa2";
    return {
      borderColor: hex,
      color: hex,
      ...(variant === "filled" && {
        backgroundColor: hex,
        color: "common.white",
      }),
    };
  }, [retryPhase, variant]);

  // Override color to "default" when retryPhase is set (custom colors via sx)
  const effectiveMap = useMemo(() => {
    if (!retryPhase) return STATUS_MAP;
    const entry = STATUS_MAP[status];
    return { ...STATUS_MAP, [status]: { ...entry, color: "default" as const } };
  }, [retryPhase, status]);

  return (
    <StatusChip
      status={status}
      statusMap={effectiveMap}
      variant={variant}
      size={size}
      label={label}
      sx={retrySx}
    />
  );
};
